import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/utils/supabaseClient';
import {
  DEFAULT_MAINTENANCE_ALLOWED_PATHS,
  DEFAULT_MAINTENANCE_ALLOWED_ROLES,
  normalizeMaintenanceSetting,
  resolveMaintenanceOverride,
} from '@/lib/maintenanceAccess.js';
import { CLIENT_PUBLICATION_MODE_VALUE } from '@/lib/publicationModeConfig.js';
import {
  PUBLICATION_MODES,
  resolvePublicationState,
} from '../../shared/publicationMode.js';

const MaintenanceContext = createContext({
  isActive: true,
  message: '',
  allowedPaths: DEFAULT_MAINTENANCE_ALLOWED_PATHS,
  allowedRoles: DEFAULT_MAINTENANCE_ALLOWED_ROLES,
  maintenanceStateAvailable: false,
  configuredPublicationMode: PUBLICATION_MODES.MAINTENANCE,
  effectivePublicationMode: PUBLICATION_MODES.MAINTENANCE,
  publicationModeReason: 'configuration_missing',
  isPublicationFailClosed: true,
  isEffectiveMaintenance: true,
  isLoading: true,
  error: null,
  refresh: async () => {},
});

const maintenanceOverride = resolveMaintenanceOverride(
  import.meta.env?.VITE_MAINTENANCE_OVERRIDE,
  { isProduction: Boolean(import.meta.env?.PROD) },
);

const maintenanceDebugLoggingEnabled = (() => {
  const explicitValue = import.meta.env?.VITE_DEBUG_MAINTENANCE;
  if (explicitValue !== undefined) {
    const normalized = String(explicitValue).trim().toLowerCase();
    return ['on', 'true', '1', 'yes'].includes(normalized);
  }

  return Boolean(import.meta.env?.DEV);
})();

export const MaintenanceProvider = ({ children }) => {
  const [state, setState] = useState({
    isActive: true,
    message: '',
    allowedPaths: [...DEFAULT_MAINTENANCE_ALLOWED_PATHS],
    allowedRoles: [...DEFAULT_MAINTENANCE_ALLOWED_ROLES],
    maintenanceStateAvailable: false,
    isLoading: true,
    error: null,
  });

  const applySetting = useCallback((setting) => {
    setState((previous) => ({
      ...previous,
      ...setting,
      isLoading: false,
      error: null,
    }));
  }, []);

  const fetchSetting = useCallback(async () => {
    if (maintenanceOverride.value !== null) {
      applySetting({
        isActive: maintenanceOverride.value,
        message: maintenanceOverride.value
          ? 'Modalita manutenzione forzata via VITE_MAINTENANCE_OVERRIDE.'
          : '',
        allowedPaths: [...DEFAULT_MAINTENANCE_ALLOWED_PATHS],
        allowedRoles: [...DEFAULT_MAINTENANCE_ALLOWED_ROLES],
        maintenanceStateAvailable: true,
      });
      return;
    }

    if (maintenanceOverride.ignored && maintenanceDebugLoggingEnabled) {
      console.warn('VITE_MAINTENANCE_OVERRIDE=off ignorato in production.');
    }

    setState((previous) => ({ ...previous, isLoading: true, error: null }));
    const { data, error } = await supabase
      .from('maintenance_settings')
      .select('is_active, message, allowed_paths, allowed_roles')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      if (maintenanceDebugLoggingEnabled) {
        console.warn('maintenance_settings fetch fallback:', error);
      }
      setState((previous) => ({
        ...previous,
        isActive: true,
        allowedPaths: [...DEFAULT_MAINTENANCE_ALLOWED_PATHS],
        allowedRoles: [...DEFAULT_MAINTENANCE_ALLOWED_ROLES],
        maintenanceStateAvailable: false,
        isLoading: false,
        error,
      }));
      return;
    }

    applySetting(normalizeMaintenanceSetting(data));
  }, [applySetting]);

  useEffect(() => {
    let isMounted = true;

    fetchSetting();

    if (maintenanceOverride.value !== null) {
      return () => {
        isMounted = false;
      };
    }

    const channel = supabase
      .channel('maintenance_settings_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'maintenance_settings',
        },
        async () => {
          if (!isMounted) return;
          await fetchSetting();
        },
      )
      .subscribe();

    return () => {
      isMounted = false;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [fetchSetting]);

  const value = useMemo(() => {
    const publicationState = resolvePublicationState({
      configuredValue: CLIENT_PUBLICATION_MODE_VALUE,
      maintenanceActive: state.isActive,
      maintenanceStateAvailable: state.maintenanceStateAvailable,
    });

    return {
      ...state,
      configuredPublicationMode: publicationState.configuredMode,
      effectivePublicationMode: publicationState.effectiveMode,
      publicationModeReason: publicationState.reason,
      isPublicationFailClosed: publicationState.isFailClosed,
      isEffectiveMaintenance:
        publicationState.effectiveMode === PUBLICATION_MODES.MAINTENANCE,
      refresh: fetchSetting,
    };
  }, [state, fetchSetting]);

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = () => useContext(MaintenanceContext);

export default MaintenanceContext;
